(function (root, factory) {
  const stateModule = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = stateModule;
  }

  if (root) {
    root.MarineMaxAIChatState = stateModule;
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  const views = Object.freeze({
    CHAT_HOME: 'chat-home',
    CHAT_CONVERSATION: 'chat-conversation',
    DEALER_PRICING: 'dealer-pricing',
    EMAIL_INPUT: 'email-input',
    FREEFORM_PROMPT: 'freeform-prompt',
    HUBSPOT_FORM: 'hubspot-form',
    HUBSPOT_SUCCESS: 'hubspot-success',
    PRICING_NAME: 'pricing-name',
    QUOTE: 'quote',
    VERIFICATION_CODE: 'verification-code',
    VERIFICATION_METHOD: 'verification-method',
  });
  const knownViews = new Set(Object.values(views));
  const messageTypes = Object.freeze({
    ASSISTANT: 'assistant',
    ERROR: 'error',
    TYPING: 'typing',
    USER: 'user',
  });
  const messageFormats = Object.freeze({
    MARKDOWN: 'markdown',
    TEXT: 'text',
  });
  const knownMessageTypes = new Set(Object.values(messageTypes));
  const knownMessageFormats = new Set(Object.values(messageFormats));

  function assertKnownView(view) {
    if (!knownViews.has(view)) {
      throw new Error(`Unknown AI chat view: ${view}`);
    }
  }

  function assertKnownMessageType(type) {
    if (!knownMessageTypes.has(type)) {
      throw new Error(`Unknown AI chat message type: ${type}`);
    }
  }

  function assertKnownMessageFormat(format) {
    if (!knownMessageFormats.has(format)) {
      throw new Error(`Unknown AI chat message format: ${format}`);
    }
  }

  function createMessageStore() {
    let messages = [];
    let nextId = 1;

    function createMessage(id, type, content, format) {
      assertKnownMessageType(type);
      assertKnownMessageFormat(format);

      return Object.freeze({
        id,
        type,
        content: String(content ?? ''),
        format,
      });
    }

    function add(type, content = '', format = messageFormats.MARKDOWN) {
      const message = createMessage(nextId, type, content, format);
      nextId += 1;
      messages.push(message);
      return message;
    }

    function get(id) {
      return messages.find((message) => message.id === id) || null;
    }

    function update(id, content) {
      const index = messages.findIndex((message) => message.id === id);
      if (index === -1) return null;

      const currentMessage = messages[index];
      const updatedMessage = createMessage(
        currentMessage.id,
        currentMessage.type,
        content,
        currentMessage.format,
      );
      messages[index] = updatedMessage;
      return updatedMessage;
    }

    function remove(id) {
      const index = messages.findIndex((message) => message.id === id);
      if (index === -1) return false;

      messages.splice(index, 1);
      return true;
    }

    function replace(nextMessages = []) {
      if (!Array.isArray(nextMessages)) {
        throw new TypeError('AI chat messages must be an array');
      }

      const seenIds = new Set();
      let highestId = 0;
      messages = nextMessages.map((message) => {
        const id = Number(message && message.id);
        if (!Number.isInteger(id) || id < 1 || seenIds.has(id)) {
          throw new Error(`Invalid AI chat message id: ${id}`);
        }

        seenIds.add(id);
        highestId = Math.max(highestId, id);
        return createMessage(id, message.type, message.content, message.format);
      });
      nextId = Math.max(nextId, highestId + 1);
      return snapshot();
    }

    function clear() {
      messages = [];
    }

    function snapshot() {
      return Object.freeze(messages.slice());
    }

    return Object.freeze({
      add,
      clear,
      get,
      remove,
      replace,
      snapshot,
      update,
    });
  }

  function createStateManager(options = {}) {
    const requestedHistoryLimit = Number(options.historyLimit);
    const historyLimit = Number.isFinite(requestedHistoryLimit)
      ? Math.max(1, Math.floor(requestedHistoryLimit))
      : 10;
    const initialView = options.initialView || views.CHAT_HOME;
    const history = [];
    let currentView = initialView;

    assertKnownView(initialView);

    function setView(nextView) {
      assertKnownView(nextView);
      currentView = nextView;
      return currentView;
    }

    function push(snapshot = {}) {
      const entry = Object.freeze({
        ...snapshot,
        view: currentView,
      });
      history.push(entry);

      if (history.length > historyLimit) {
        history.shift();
      }

      return entry;
    }

    function pop() {
      const entry = history.pop() || null;
      if (entry) {
        currentView = entry.view;
      }
      return entry;
    }

    function reset(nextView = initialView) {
      assertKnownView(nextView);
      history.length = 0;
      currentView = nextView;
    }

    return Object.freeze({
      get currentView() {
        return currentView;
      },
      get historyLength() {
        return history.length;
      },
      pop,
      push,
      reset,
      setView,
    });
  }

  return Object.freeze({
    createMessageStore,
    createStateManager,
    messageFormats,
    messageTypes,
    views,
  });
});
